import { useState } from 'react'
import { Pencil, Plus, ShieldAlert } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, PermissionGate,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import {
  useRecruitmentConfigMutations, useRecruitmentSettings, useRecruitmentSources,
  useRejectionReasons,
} from '../api'
import type { RecruitmentSettingView } from '../types'

/**
 * Sources, rejection reasons and the per-entity hiring controls.
 *
 * The two empty lists here are empty on purpose, and the screen says so rather than looking
 * unfinished. A shipped "LinkedIn" would land in this year's funnel analytics as though
 * somebody had chosen it, and rejection reasons are the record an employer produces to show
 * a rejection was lawful.
 */
export function RecruitmentSettingsPage() {
  useBreadcrumb()
  const sources = useRecruitmentSources()
  const reasons = useRejectionReasons()
  const settings = useRecruitmentSettings()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const {
    saveSource, saveReason, saveSetting, retireSource, retireReason,
  } = useRecruitmentConfigMutations()

  const [sourceDraft, setSourceDraft] = useState<{
    id?: string; code: string; name: string; paid: boolean
  } | null>(null)
  const [reasonDraft, setReasonDraft] = useState<{
    id?: string; code: string; name: string; appliesAtStage: string; reconsider: boolean
  } | null>(null)
  const [retiring, setRetiring] = useState<
    { kind: 'source' | 'reason'; id: string; name: string } | null
  >(null)
  const [settingDraft, setSettingDraft] = useState<RecruitmentSettingView | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Recruitment settings"
        subtitle="Sources, rejection reasons, and what stops a hire."
      />

      <PageHeader
        title="Sources"
        subtitle="Where candidates come from."
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null)
              setSourceDraft({ code: '', name: '', paid: false })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New source
          </Button>
        }
      />
      <Card>
        {sources.isPending && <Skeleton className="m-4 h-20" />}
        {sources.isError && <ErrorState error={sources.error} onRetry={() => void sources.refetch()} />}
        {sources.isSuccess && sources.data.length === 0 && (
          <EmptyState
            title="No source is defined"
            description="None ships with this system, deliberately. A seeded channel would show up in this year's funnel as though somebody had chosen it."
          />
        )}
        {sources.isSuccess && sources.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Costs money</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {sources.data.map((source) => (
                  <tr key={source.id} className={`border-b border-slate-100 ${source.active ? '' : 'text-slate-400'}`}>
                    <td className="p-3 font-mono">{source.code}</td>
                    <td className="p-3">{source.name}</td>
                    <td className="p-3 text-slate-600">{source.paid ? 'Yes' : 'No'}</td>
                    <td className="p-3 text-end">
                      <PermissionGate permission="recruitment:configure">
                        <Button
                          type="button" variant="ghost" size="sm"
                          aria-label={`Edit ${source.name}`}
                          onClick={() => {
                            setError(null)
                            setSourceDraft({
                              id: source.id, code: source.code, name: source.name,
                              paid: source.paid,
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {source.active && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => {
                              setError(null)
                              setRetiring({ kind: 'source', id: source.id, name: source.name })
                            }}
                          >
                            Retire
                          </Button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PageHeader
        title="Rejection reasons"
        subtitle="Why applications end."
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null)
              setReasonDraft({ code: '', name: '', appliesAtStage: '', reconsider: true })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New reason
          </Button>
        }
      />
      <Card>
        {reasons.isPending && <Skeleton className="m-4 h-20" />}
        {reasons.isSuccess && reasons.data.length === 0 && (
          <EmptyState
            title="No rejection reason is defined"
            description="None ships with this system. These carry legal weight in several jurisdictions — they are the record that shows a rejection was lawful — so the list has to be yours."
          />
        )}
        {reasons.isSuccess && reasons.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Used at</th>
                  <th className="p-3 text-start font-medium">May reapply</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {reasons.data.map((reason) => (
                  <tr key={reason.id} className={`border-b border-slate-100 ${reason.active ? '' : 'text-slate-400'}`}>
                    <td className="p-3 font-mono">{reason.code}</td>
                    <td className="p-3">{reason.name}</td>
                    <td className="p-3 text-slate-600">{reason.appliesAtStage ?? 'Any stage'}</td>
                    <td className="p-3 text-slate-600">{reason.reconsider ? 'Yes' : 'No'}</td>
                    <td className="p-3 text-end">
                      <PermissionGate permission="recruitment:configure">
                        <Button
                          type="button" variant="ghost" size="sm"
                          aria-label={`Edit ${reason.name}`}
                          onClick={() => {
                            setError(null)
                            setReasonDraft({
                              id: reason.id, code: reason.code, name: reason.name,
                              appliesAtStage: reason.appliesAtStage ?? '',
                              reconsider: reason.reconsider,
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {reason.active && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => {
                              setError(null)
                              setRetiring({ kind: 'reason', id: reason.id, name: reason.name })
                            }}
                          >
                            Retire
                          </Button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this'}?`}
        description={retiring?.kind === 'reason'
          ? 'It stops being offered on new rejections. Every application already closed under it keeps naming it — these carry legal weight, so this is never a delete.'
          : 'It stops being offered. Every candidate who came through it keeps naming it, so this year\'s funnel still adds up.'}
        confirmLabel="Retire it"
        loading={retireSource.isPending || retireReason.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          const call = retiring.kind === 'source' ? retireSource : retireReason
          call.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That could not be retired.'),
          })
        }}
      />

      <PageHeader title="Hiring controls" subtitle="What stops a candidate becoming an employee." />
      <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Both controls are on by default. Turning off the PRO block lets somebody start work
        before the paperwork that permits them to work is done; turning off the accepted-offer
        requirement lets a hire happen against a salary nobody has agreed to.
      </p>
      <Card>
        {settings.isPending && <Skeleton className="m-4 h-20" />}
        {settings.isSuccess && settings.data.length === 0 && (
          <EmptyState
            title="No entity has its own controls yet"
            description="Each entity gets both controls on, written down as a row, the first time a hire is attempted for it."
          />
        )}
        {settings.isSuccess && settings.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Entity</th>
                  <th className="p-3 text-start font-medium">PRO case blocks a hire</th>
                  <th className="p-3 text-start font-medium">Accepted offer required</th>
                  <th className="p-3 text-start font-medium">Offer validity</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {settings.data.map((setting) => (
                  <tr key={setting.id} className="border-b border-slate-100">
                    <td className="p-3 font-mono text-xs">
                      {entities.data?.find((entity) => entity.id === setting.legalEntityId)?.name
                        ?? setting.legalEntityId}
                    </td>
                    <td className="p-3">
                      {setting.onboardingBlockedByProCase
                        ? <span className="text-slate-700">Yes</span>
                        : <span className="text-amber-700">No — hires are not blocked</span>}
                    </td>
                    <td className="p-3">
                      {setting.requireAcceptedOfferToHire
                        ? <span className="text-slate-700">Yes</span>
                        : <span className="text-amber-700">No</span>}
                    </td>
                    <td className="p-3 text-slate-600">
                      {setting.defaultOfferValidityDays == null
                        ? 'Set on each offer' : `${setting.defaultOfferValidityDays} days`}
                    </td>
                    <td className="p-3 text-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => { setError(null); setSettingDraft(setting) }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={sourceDraft != null}
        onOpenChange={(open) => { if (!open) setSourceDraft(null) }}
        title={sourceDraft?.id ? 'Edit recruitment source' : 'New recruitment source'}
        submitLabel="Save source"
        loading={saveSource.isPending}
        error={error}
        onSubmit={() => {
          if (!sourceDraft) return
          void (async () => {
            setError(null)
            try {
              await saveSource.mutateAsync({ ...sourceDraft })
              setSourceDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That source could not be saved')
            }
          })()
        }}
      >
        {sourceDraft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="source-code">Code</Label>
                <Input
                  id="source-code" value={sourceDraft.code}
                  onChange={(event) => setSourceDraft({
                    ...sourceDraft, code: event.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label htmlFor="source-name">Name</Label>
                <Input
                  id="source-name" value={sourceDraft.name}
                  onChange={(event) =>
                    setSourceDraft({ ...sourceDraft, name: event.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sourceDraft.paid}
                onChange={(event) =>
                  setSourceDraft({ ...sourceDraft, paid: event.target.checked })}
              />
              This source is paid for, so postings can carry a cost
            </label>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={reasonDraft != null}
        onOpenChange={(open) => { if (!open) setReasonDraft(null) }}
        title={reasonDraft?.id ? 'Edit rejection reason' : 'New rejection reason'}
        submitLabel="Save reason"
        loading={saveReason.isPending}
        error={error}
        onSubmit={() => {
          if (!reasonDraft) return
          void (async () => {
            setError(null)
            try {
              await saveReason.mutateAsync({
                ...reasonDraft, appliesAtStage: reasonDraft.appliesAtStage || null,
              })
              setReasonDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That reason could not be saved')
            }
          })()
        }}
      >
        {reasonDraft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="reason-code">Code</Label>
                <Input
                  id="reason-code" value={reasonDraft.code}
                  onChange={(event) => setReasonDraft({
                    ...reasonDraft, code: event.target.value.toUpperCase() })}
                />
              </div>
              <div>
                <Label htmlFor="reason-name">Name</Label>
                <Input
                  id="reason-name" value={reasonDraft.name}
                  onChange={(event) =>
                    setReasonDraft({ ...reasonDraft, name: event.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="reason-stage">Used at stage</Label>
                <Input
                  id="reason-stage" value={reasonDraft.appliesAtStage}
                  onChange={(event) =>
                    setReasonDraft({ ...reasonDraft, appliesAtStage: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  Left blank, it is offered at every stage.
                </p>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={reasonDraft.reconsider}
                onChange={(event) =>
                  setReasonDraft({ ...reasonDraft, reconsider: event.target.checked })}
              />
              This person may be considered again later
            </label>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={settingDraft != null}
        onOpenChange={(open) => { if (!open) setSettingDraft(null) }}
        title="Hiring controls"
        description="These decide what stops a candidate becoming an employee."
        submitLabel="Save controls"
        loading={saveSetting.isPending}
        error={error}
        onSubmit={() => {
          if (!settingDraft) return
          void (async () => {
            setError(null)
            try {
              await saveSetting.mutateAsync({ ...settingDraft })
              setSettingDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'Those controls could not be saved')
            }
          })()
        }}
      >
        {settingDraft && (
          <div className="space-y-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={settingDraft.onboardingBlockedByProCase}
                onChange={(event) => setSettingDraft({
                  ...settingDraft, onboardingBlockedByProCase: event.target.checked })}
              />
              <span>
                An open PRO case with outstanding required documents stops the hire. Turning
                this off lets somebody start work before the paperwork that permits them to
                work is done.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={settingDraft.requireAcceptedOfferToHire}
                onChange={(event) => setSettingDraft({
                  ...settingDraft, requireAcceptedOfferToHire: event.target.checked })}
              />
              <span>
                An accepted offer is required before a hire. Turning this off lets a hire
                happen against a salary nobody has agreed to.
              </span>
            </label>
            <div>
              <Label htmlFor="setting-validity">Default offer validity, days</Label>
              <Input
                id="setting-validity" type="number"
                value={settingDraft.defaultOfferValidityDays ?? ''}
                onChange={(event) => setSettingDraft({
                  ...settingDraft,
                  defaultOfferValidityDays: event.target.value === ''
                    ? null : Number(event.target.value),
                })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Blank means every offer's validity is set by hand. There is no industry figure
                to borrow here.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
